import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _typeof from '@babel/runtime/helpers/esm/typeof';
import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread';
import escapeHtml from 'escape-html';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';

var CONTEXT_KEY = 'vue-helmet-context';
var isServer = typeof window === 'undefined';
var isProd = process.env.NODE_ENV === 'production';

var mapInstancesToProps = function mapInstancesToProps(instances) {
  return instances.map(function (vm) {
    var data = vm.$vnode.data;
    return data ? data.props : undefined;
  });
};

var withSideEffect = (function (reducePropsToState, handleStateChangeOnClient, mapStateOnServer) {
  if (!isProd) {
    if (typeof reducePropsToState !== 'function') {
      throw new Error('Expected reducePropsToState to be a function.');
    }

    if (typeof handleStateChangeOnClient !== 'function') {
      throw new Error('Expected handleStateChangeOnClient to be a function.');
    }

    if (typeof mapStateOnServer !== 'undefined' && typeof mapStateOnServer !== 'function') {
      throw new Error('Expected mapStateOnServer to either be undefined or a function.');
    }
  }

  return function (Component, providerName) {
    if (!isProd) {
      if (_typeof(Component) !== 'object' || typeof Component.render !== 'function') {
        throw new Error('Expected Component to be a Vue Component.');
      }
    }

    var Provider = {
      name: providerName,
      inject: {
        parentProvider: {
          from: CONTEXT_KEY,
          default: null
        }
      },
      props: {
        context: {
          type: Object,
          default: function _default() {
            return {};
          }
        }
      },
      data: function data() {
        return {
          instances: [],
          last: null
        };
      },
      methods: {
        update: function update() {
          var _this = this;

          var current;
          this.last = this.$nextTick().then(function () {
            if (_this.last !== current) return;
            var nextState = reducePropsToState(mapInstancesToProps(_this.instances));
            handleStateChangeOnClient(nextState);
            _this.context.state = nextState;
          });
          current = this.last;
        }
      },
      created: function created() {
        if (this.parentProvider) throw new Error('Nesting of side-efect Providers is not allowed');
        var nextState = reducePropsToState(mapInstancesToProps(this.instances));
        if (mapStateOnServer) nextState = mapStateOnServer(nextState);
        this.context.state = nextState;
      },
      provide: function provide() {
        return _defineProperty({}, CONTEXT_KEY, {
          instances: this.instances,
          staticContext: this.$props.context,
          provider: this
        });
      },
      render: function render() {
        return this.$slots.default[0];
      }
    };
    var NullComponent = {
      render: function render() {
        return this.$slots.default;
      }
    };
    var Consumer = {
      name: "".concat(Component.name, "-side-effect"),
      inject: [CONTEXT_KEY],
      props: Component.props,
      created: function created() {
        var context = this[CONTEXT_KEY];

        if (!context) {
          throw new Error('cannot render a side-effect without a side-effect Provider');
        }

        var instances = context.instances,
            staticContext = context.staticContext;
        instances.push(this);
        if (!isServer) return;
        var nextState = reducePropsToState(mapInstancesToProps(instances));
        if (mapStateOnServer) nextState = mapStateOnServer(nextState);
        staticContext.state = nextState;
      },
      mounted: function mounted() {
        this[CONTEXT_KEY].provider.update();
      },
      beforeUpdate: function beforeUpdate() {
        this[CONTEXT_KEY].provider.update();
      },
      beforeDestroy: function beforeDestroy() {
        var _this$CONTEXT_KEY = this[CONTEXT_KEY],
            instances = _this$CONTEXT_KEY.instances,
            provider = _this$CONTEXT_KEY.provider;
        instances.splice(instances.indexOf(this), 1);
        provider.$forceUpdate();
      },
      render: function render(h) {
        var _this2 = this;

        return h(Component, {
          props: this.$props,
          attrs: this.$attrs,
          listeners: this.$on,
          scopedSlots: this.$scopedSlots
        }, Object.keys(this.$slots).map(function (slot) {
          return {
            slot: _this2.$slots[slot],
            render: function render() {
              return h(NullComponent, _this2.$slots[slot]);
            }
          };
        }));
      }
    };
    return {
      Provider: Provider,
      Consumer: Consumer
    };
  };
});

var ATTRIBUTE_NAMES = {
  BODY: 'bodyAttributes',
  HTML: 'htmlAttributes',
  TITLE: 'titleAttributes'
};
var VALID_TAG_NAMES = ['base', 'body', 'html', 'link', 'meta', 'noscript', 'script', 'style', 'title'];
var HELMET_PROPS = {
  DEFAULT_TITLE: 'defaultTitle',
  HANDLE_CHANGE_CLIENT_STATE: 'handleClientStateChange',
  TITLE_TEMPLATE: 'titleTemplate'
};
var SELF_CLOSING_TAGS = ['noscript', 'script', 'style'];
var HELMET_ATTRIBUTE = 'data-vue-helmet';

function updateAttributes(tagName, attributes) {
  var elementTag = document.getElementsByTagName(tagName)[0];

  if (!elementTag) {
    return;
  }

  var helmetAttributeString = elementTag.getAttribute(HELMET_ATTRIBUTE);
  var helmetAttributes = helmetAttributeString ? helmetAttributeString.split(',') : [];

  var attributesToRemove = _toConsumableArray(helmetAttributes);

  var keys = Object.keys(attributes);
  keys.forEach(function (attribute) {
    var value = attributes[attribute];
    if (value == null) return;

    if (elementTag.getAttribute(attribute) !== value) {
      elementTag.setAttribute(attribute, value);
    }

    if (helmetAttributes.indexOf(attribute) === -1) {
      helmetAttributes.push(attribute);
    }

    var ix = attributesToRemove.indexOf(attribute);

    if (ix !== -1) {
      attributesToRemove.splice(ix, 1);
    }
  });
  attributesToRemove.forEach(function (attr) {
    return elementTag.removeAttribute(attr);
  });

  if (helmetAttributes.length === attributesToRemove.length) {
    elementTag.removeAttribute(HELMET_ATTRIBUTE);
    return;
  }

  var joinedKeys = keys.sort().join(',');

  if (helmetAttributeString !== joinedKeys) {
    elementTag.setAttribute(HELMET_ATTRIBUTE, joinedKeys);
  }
}
function updateTitle(title, attributes) {
  if (title !== undefined && document.title !== title) {
    document.title = title;
  }

  updateAttributes('title', attributes);
}
function updateTags(type, tags) {
  var headElement = document.head || document.querySelector('head');
  if (!headElement) throw new Error('document is missing head');
  var tagNodes = headElement.querySelectorAll("".concat(type, "[").concat(HELMET_ATTRIBUTE, "]"));
  var oldTags = Array.prototype.slice.call(tagNodes);
  var newTags = [];
  var indexToDelete;

  if (tags) {
    tags.forEach(function (tag) {
      var newElement = document.createElement(type);
      Object.keys(tag).forEach(function (rawAttr) {
        var rawValue = tag[rawAttr];
        var value = rawValue;
        var attribute = rawAttr;

        if (attribute === 'innerHTML') {
          newElement.innerHTML = value;
        } else if (attribute === 'cssText') {
          var _ref = newElement,
              styleSheet = _ref.styleSheet;

          if (styleSheet) {
            styleSheet.cssText = value;
          } else {
            newElement.appendChild(document.createTextNode(value));
          }
        } else {
          newElement.setAttribute(attribute, value);
        }
      });
      newElement.setAttribute(HELMET_ATTRIBUTE, 'true'); // Remove a duplicate tag from domTagstoRemove, so it isn't cleared.

      if (oldTags.some(function (existingTag, index) {
        indexToDelete = index;
        return newElement.isEqualNode(existingTag);
      })) {
        oldTags.splice(indexToDelete, 1);
      } else {
        newTags.push(newElement);
      }
    });
  }

  oldTags.forEach(function (tag) {
    var parentNode = tag.parentNode;
    if (!parentNode) throw new Error('attempted to remove tag without parent node');else parentNode.removeChild(tag);
  });
  newTags.forEach(function (tag) {
    return headElement.appendChild(tag);
  });
  return {
    oldTags: oldTags,
    newTags: newTags
  };
}

/* eslint-disable no-console */
var warn = function warn(msg) {
  if (console && typeof console.warn === 'function') console.warn(msg);
};
/* eslint-enable no-console */


var exceptions = ['defaultTitle', 'titleTemplate', 'handleClientStateChange'];

function getInnermostProperty(propsList, property) {
  for (var i = propsList.length - 1; i >= 0; i -= 1) {
    var props = propsList[i];
    var prop = props[property];

    if (exceptions.indexOf(property) !== -1) {
      if (prop !== undefined) return prop;
    } else if ({}.hasOwnProperty.call(props, property)) {
      return props[property];
    }
  }

  return null;
}

function getTitleFromPropsList(propsList) {
  var innermostTitle = getInnermostProperty(propsList, 'title');
  var innermostTemplate = getInnermostProperty(propsList, HELMET_PROPS.TITLE_TEMPLATE);

  if (innermostTemplate && innermostTitle) {
    // use function arg to avoid need to escape $ characters
    return innermostTemplate.replace(/%s/g, function () {
      return innermostTitle;
    });
  }

  var innermostDefaultTitle = getInnermostProperty(propsList, HELMET_PROPS.DEFAULT_TITLE);
  return innermostTitle == null ? innermostDefaultTitle || undefined : innermostTitle;
}

var getHandleClientStateChange = function getHandleClientStateChange(propsList) {
  return getInnermostProperty(propsList, HELMET_PROPS.HANDLE_CHANGE_CLIENT_STATE) || function () {};
};

function getAttributesFromPropsList(tagType, propsList) {
  return propsList.filter(function (props) {
    return props[tagType] !== undefined;
  }).map(function (props) {
    return props[tagType];
  }).reduce(function (tagAttrs, current) {
    return _objectSpread({}, tagAttrs, current);
  }, {});
}

function getBaseTagFromPropsList(primaryAttributes, propsList) {
  return propsList.map(function (props) {
    return props.base;
  }).filter(function (b) {
    return b;
  }).reverse().reduce(function (innermostBaseTag, tag) {
    if (!innermostBaseTag.length && tag) {
      var keys = Object.keys(tag);

      for (var i = 0; i < keys.length; i += 1) {
        var attributeKey = keys[i];
        var lowerCaseAttributeKey = attributeKey.toLowerCase();

        if (primaryAttributes.indexOf(lowerCaseAttributeKey) !== -1 && tag[lowerCaseAttributeKey]) {
          return innermostBaseTag.concat(tag);
        }
      }
    }

    return innermostBaseTag;
  }, []);
}

function getTagsFromPropsList(tagName, primaryAttributes, propsList) {
  // Calculate list of tags, giving priority innermost component (end of the propslist)
  var approvedSeenTags = {};
  return propsList.map(function (props) {
    return props[tagName];
  }).filter(function (tag) {
    return Array.isArray(tag);
  }).reverse().reduce(function (approvedTags, instanceTags) {
    var instanceSeenTags = {};
    instanceTags.filter(function (data) {
      var tag = _objectSpread({}, data, data.attrs, data.domProps);

      var primaryAttributeKey;
      var keys = Object.keys(tag);
      keys.forEach(function (attributeKey) {
        var lowerCaseAttributeKey = attributeKey.toLowerCase(); // Special rule with link tags, since rel and href are both primary tags
        // rel takes priority

        if (primaryAttributes.indexOf(lowerCaseAttributeKey) !== -1 && !(primaryAttributeKey === 'rel' && tag[primaryAttributeKey].toLowerCase() === 'canonical') && !(lowerCaseAttributeKey === 'rel' && tag[lowerCaseAttributeKey].toLowerCase() === 'stylesheet')) {
          primaryAttributeKey = lowerCaseAttributeKey;
        } // Special case for innerHTML which doesn't work lowercased


        if (primaryAttributes.indexOf(attributeKey) !== -1 && (attributeKey === 'innerHTML' || attributeKey === 'cssText' || attributeKey === 'itemProp')) {
          primaryAttributeKey = attributeKey;
        }
      });

      if (!primaryAttributeKey || !tag[primaryAttributeKey]) {
        return false;
      }

      var value = tag[primaryAttributeKey].toLowerCase();

      if (!approvedSeenTags[primaryAttributeKey]) {
        approvedSeenTags[primaryAttributeKey] = {};
      }

      if (!instanceSeenTags[primaryAttributeKey]) {
        instanceSeenTags[primaryAttributeKey] = {};
      }

      if (!approvedSeenTags[primaryAttributeKey][value]) {
        instanceSeenTags[primaryAttributeKey][value] = true;
        return true;
      }

      return false;
    }).reverse().forEach(function (tag) {
      return approvedTags.push(tag);
    }); // Update seen tags with tags from this instance

    var keys = Object.keys(instanceSeenTags);
    keys.forEach(function (attributeKey) {
      var tagUnion = _objectSpread({}, approvedSeenTags[attributeKey], instanceSeenTags[attributeKey]);

      approvedSeenTags[attributeKey] = tagUnion;
    });
    return approvedTags;
  }, []).reverse();
}

var commitTagChanges = function commitTagChanges(newState, cb) {
  var baseTag = newState.baseTag,
      bodyAttributes = newState.bodyAttributes,
      htmlAttributes = newState.htmlAttributes,
      linkTags = newState.linkTags,
      metaTags = newState.metaTags,
      noscriptTags = newState.noscriptTags,
      handleClientStateChange = newState.handleClientStateChange,
      scriptTags = newState.scriptTags,
      styleTags = newState.styleTags,
      title = newState.title,
      titleAttributes = newState.titleAttributes;
  updateAttributes('body', bodyAttributes);
  updateAttributes('html', htmlAttributes);
  updateTitle(title, titleAttributes);
  var tagUpdates = {
    baseTag: updateTags('base', baseTag),
    linkTags: updateTags('link', linkTags),
    metaTags: updateTags('meta', metaTags),
    noscriptTags: updateTags('noscript', noscriptTags),
    scriptTags: updateTags('script', scriptTags),
    styleTags: updateTags('style', styleTags)
  };
  var addedTags = {};
  var removedTags = {};
  Object.keys(tagUpdates).forEach(function (tagType) {
    var _tagUpdates$tagType = tagUpdates[tagType],
        newTags = _tagUpdates$tagType.newTags,
        oldTags = _tagUpdates$tagType.oldTags;

    if (newTags.length) {
      addedTags[tagType] = newTags;
    }

    if (oldTags.length) {
      removedTags[tagType] = tagUpdates[tagType].oldTags;
    }
  });
  if (cb) cb();
  handleClientStateChange(newState, addedTags, removedTags);
};

function generateElementAttributesAsString(attributes) {
  return Object.keys(attributes).map(function (key) {
    return typeof attributes[key] !== 'undefined' ? "".concat(key, "=\"").concat(escapeHtml(attributes[key]), "\"") : "".concat(key);
  }).join(' ');
}

var generateTitleAsString = function generateTitleAsString(title, attributes) {
  var attributeString = generateElementAttributesAsString(attributes);
  var attrs = attributeString ? " ".concat(attributeString) : '';
  return "<title ".concat(HELMET_ATTRIBUTE, "=\"true\"").concat(attrs, ">").concat(escapeHtml(title), "</title>");
};

var generateTagsAsString = function generateTagsAsString(type, tags) {
  return tags.map(function (tag) {
    var attributeHtml = Object.keys(tag).filter(function (attribute) {
      return attribute !== 'innerHTML' && attribute !== 'cssText';
    }).map(function (attribute) {
      return tag[attribute] === undefined ? attribute : "".concat(attribute, "=\"").concat(escapeHtml(tag[attribute]), "\"");
    }).join(' ');
    if (attributeHtml) attributeHtml = " ".concat(attributeHtml);
    var isSelfClosing = SELF_CLOSING_TAGS.indexOf(type) === -1;
    return "<".concat(type, " ").concat(HELMET_ATTRIBUTE, "=\"true\"").concat(attributeHtml).concat(isSelfClosing ? '/>' : ">".concat(tag.innerHTML || tag.cssText || '', "</").concat(type, ">"));
  }).join('');
};

function convertVNodeDataToMap(data) {
  var initAttributes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var style = data.style,
      className = data.class,
      attrs = data.attrs,
      domProps = data.domProps;
  var initStyle = initAttributes.style,
      initClassName = initAttributes.class,
      initAttrs = initAttributes.attrs,
      initDomProps = initAttributes.domProps;

  var _initDomProps$domProp = _objectSpread({}, initDomProps, domProps),
      cssText = _initDomProps$domProp.cssText,
      innerHTML = _initDomProps$domProp.innerHTML;

  var result = {};
  if (style || initStyle) result.style = style || initStyle;
  if (innerHTML) result.innerHTML = innerHTML;
  if (cssText) result.cssText = cssText;
  if (className || initClassName) result.class = className || initClassName;
  return _objectSpread({}, result, initAttrs, attrs);
}

var handleClientStateChange = commitTagChanges;

var mapStateOnServer = function mapStateOnServer(_ref) {
  var baseTag = _ref.baseTag,
      bodyAttributes = _ref.bodyAttributes,
      htmlAttributes = _ref.htmlAttributes,
      linkTags = _ref.linkTags,
      metaTags = _ref.metaTags,
      noscriptTags = _ref.noscriptTags,
      scriptTags = _ref.scriptTags,
      styleTags = _ref.styleTags,
      _ref$title = _ref.title,
      title = _ref$title === void 0 ? '' : _ref$title,
      titleAttributes = _ref.titleAttributes;
  return {
    base: generateTagsAsString('base', baseTag),
    bodyAttributes: generateElementAttributesAsString(bodyAttributes),
    htmlAttributes: generateElementAttributesAsString(htmlAttributes),
    link: generateTagsAsString('link', linkTags),
    meta: generateTagsAsString('meta', metaTags),
    noscript: generateTagsAsString('noscript', noscriptTags),
    script: generateTagsAsString('script', scriptTags),
    style: generateTagsAsString('style', styleTags),
    title: generateTitleAsString(title, titleAttributes)
  };
};

var reducePropsToState = function reducePropsToState(propsList) {
  return {
    baseTag: getBaseTagFromPropsList(['href'], propsList),
    bodyAttributes: getAttributesFromPropsList(ATTRIBUTE_NAMES.BODY, propsList),
    htmlAttributes: getAttributesFromPropsList(ATTRIBUTE_NAMES.HTML, propsList),
    linkTags: getTagsFromPropsList('link', ['rel', 'href'], propsList),
    metaTags: getTagsFromPropsList('meta', ['name', 'charset', 'http-equiv', 'property', 'itemprop'], propsList),
    noscriptTags: getTagsFromPropsList('noscript', ['innerHTML'], propsList),
    handleClientStateChange: getHandleClientStateChange(propsList),
    scriptTags: getTagsFromPropsList('script', ['src', 'innerHTML'], propsList),
    styleTags: getTagsFromPropsList('styleTags', ['cssText'], propsList),
    title: getTitleFromPropsList(propsList),
    titleAttributes: getAttributesFromPropsList(ATTRIBUTE_NAMES.TITLE, propsList)
  };
};

function mapNestedChildrenToProps(tag, nestedChildren) {
  if (!nestedChildren || !nestedChildren.length) {
    return null;
  }

  if (nestedChildren.length !== 1) {
    throw new Error("expected <".concat(tag, " /> element to have a single string as the default slot. Refer to our API for more information."));
  }

  var _nestedChildren = _slicedToArray(nestedChildren, 1),
      textNode = _nestedChildren[0];

  var text = textNode.text;

  switch (tag) {
    case 'script':
    case 'noscript':
      return {
        innerHTML: text
      };

    case 'style':
      return {
        cssText: text
      };

    default:
      throw new Error("<".concat(tag, " /> elements are self-closing and can not contain children. Refer to our API for more information."));
  }
}

function flattenArrayTypeChildren(_ref) {
  var tag = _ref.tag,
      arrayTypeChildren = _ref.arrayTypeChildren,
      newChildData = _ref.newChildData,
      nestedChildren = _ref.nestedChildren;

  var result = _objectSpread({}, arrayTypeChildren);

  var _ref2 = newChildData || {},
      innerHTML = _ref2.innerHTML,
      rest = _objectWithoutProperties(_ref2, ["innerHTML"]);

  var nextData = tag === 'style' && innerHTML !== undefined ? _objectSpread({
    cssText: innerHTML
  }, rest) : newChildData;
  result[tag] = [].concat(_toConsumableArray(arrayTypeChildren[tag] || []), [_objectSpread({}, mapNestedChildrenToProps(tag, nestedChildren), nextData)]);
  return result;
}

function extractTitle(titleChildren) {
  return titleChildren && titleChildren.length ? titleChildren.map(function (child) {
    return child.text;
  }).filter(function (text) {
    return text != null;
  }).join('') : undefined;
}

function mapObjectTypeChildren(_ref3) {
  var tag = _ref3.tag,
      newProps = _ref3.newProps,
      newChildData = _ref3.newChildData,
      nestedChildren = _ref3.nestedChildren;

  var result = _objectSpread({}, newProps);

  switch (tag) {
    case 'title':
      result.title = extractTitle(nestedChildren);
      result.titleAttributes = _objectSpread({}, result.titleAttributes, newChildData);
      break;

    case 'body':
      result.bodyAttributes = _objectSpread({}, result.bodyAttributes, newChildData);
      break;

    case 'html':
      result.htmlAttributes = _objectSpread({}, result.htmlAttributes, newChildData);
      break;

    default:
      result[tag] = _objectSpread({}, result[tag], newChildData);
      break;
  }

  return result;
}

function mapArrayTypeChildrenToProps(arrayTypeChildren, newProps) {
  var newFlattenedProps = _objectSpread({}, newProps);

  Object.keys(arrayTypeChildren).forEach(function (arrayChildName) {
    var key = arrayChildName;
    newFlattenedProps[key === 'style' ? 'styleTags' : key] = arrayTypeChildren[key];
  });
  return newFlattenedProps;
}

function warnOnInvalidChildren(child, nestedChildren) {
  if (process.env.NODE_ENV === 'production') return;
  var tagName = child.tag || 'undefined';

  if (!VALID_TAG_NAMES.some(function (name) {
    return tagName === name;
  })) {
    if (typeof child.type === 'function') {
      warn('You may be attempting to nest <Helmet> components within each other, which is not allowed. Refer to our API for more information.');
      return;
    }

    warn("Only elements types ".concat(VALID_TAG_NAMES.join(', '), " are allowed. Helmet does not support rendering <").concat(child.tag || 'undefined', "> elements. Refer to our API for more information."));
    return;
  }

  if (nestedChildren && nestedChildren.length && (nestedChildren.length !== 1 || nestedChildren[0].tag !== undefined)) {
    throw new Error("Helmet expects a string as the default slot of <".concat(tagName, ">. Did you forget to wrap the slot in braces? ( <").concat(tagName, ">{``}</").concat(tagName, "> ) Refer to our API for more information."));
  }
}

function mapChildrenToProps(children, newProps) {
  var arrayTypeChildren = {};
  var result = newProps;
  children.forEach(function (child) {
    var nestedChildren = child.children,
        childData = child.data;

    if (!nestedChildren && !childData) {
      return;
    }

    var newChildData = childData ? convertVNodeDataToMap(childData) : null;
    warnOnInvalidChildren(child, nestedChildren);
    var tag = child.tag;

    switch (tag) {
      case 'link':
      case 'meta':
      case 'noscript':
      case 'script':
      case 'style':
        arrayTypeChildren = flattenArrayTypeChildren({
          tag: tag,
          arrayTypeChildren: arrayTypeChildren,
          newChildData: newChildData,
          nestedChildren: nestedChildren
        });
        break;

      case 'base':
      case 'body':
      case 'html':
      case 'title':
        result = mapObjectTypeChildren({
          tag: tag,
          newProps: result,
          newChildData: newChildData,
          nestedChildren: nestedChildren
        });
        break;

      default:
        break;
    }
  });
  result = mapArrayTypeChildrenToProps(arrayTypeChildren, result);
  return result;
}

var NullComponent = {
  props: {
    base: {},
    bodyAttributes: {},
    defaultTitle: String,
    htmlAttributes: {},
    link: Array,
    meta: Array,
    noscript: Array,
    handleClientStateChange: Function,
    script: Array,
    styleTags: Array,
    title: String,
    titleAttributes: {},
    titleTemplate: String
  },
  name: 'null-component',
  render: function render() {
    return null;
  }
};

var _withSideEffect = withSideEffect(reducePropsToState, handleClientStateChange, mapStateOnServer)(NullComponent, 'helmet-provider'),
    Provider = _withSideEffect.Provider,
    Consumer = _withSideEffect.Consumer;

var HelmetProvider = Provider;
var Helmet = {
  name: 'helmet',
  props: {
    defaultTitle: String,
    titleTemplate: String,
    handleClientStateChange: Function
  },
  render: function render(h) {
    var children = this.$slots.default;
    var newProps = this.$props;

    if (children) {
      newProps = mapChildrenToProps(children, newProps);
    }

    return h(Consumer, {
      props: newProps
    }, this.$slots.default);
  }
};

export { HelmetProvider, Helmet };
//# sourceMappingURL=index.es.js.map
